import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { 
  FileText, 
  Calendar, 
  Package, 
  TrendingUp,
  ArrowRight,
  Loader2,
  Search
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { apiGet } from '../lib/apiClient';

const CatalogHistory = ({ user }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const data = await apiGet('/api/analyses');
      setAnalyses(data?.analyses || []);
    } catch {
      console.error('Failed to fetch analyses');
    } finally {
      setLoading(false);
    }
  };

  const filteredAnalyses = analyses.filter(a => 
    (a.name || 'Single Product').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} activePage="history" />
      
      <main className="flex-1 p-6 lg:p-8 lg:pt-24">
        <div className="max-w-5xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-outfit text-3xl font-bold text-[#0B3D91]" data-testid="history-title">
                Analysis History
              </h1>
              <p className="text-slate-600">
                View and manage your past catalog analyses
              </p>
            </div>
            <Link to="/dashboard">
              <Button className="bg-[#0B3D91] hover:bg-[#082A66] text-white rounded-full" data-testid="new-analysis-btn">
                New Analysis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search analyses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-5 rounded-xl border-slate-200 focus:ring-[#1E5EFF]"
              data-testid="search-input"
            />
          </div>

          {/* Analyses List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#1E5EFF] animate-spin" />
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-outfit text-xl font-semibold text-slate-700 mb-2">
                No analyses yet
              </h3>
              <p className="text-slate-500 mb-6">
                Start by analyzing a product or uploading your catalog
              </p>
              <Link to="/dashboard">
                <Button className="bg-[#0B3D91] hover:bg-[#082A66] text-white rounded-full">
                  Start Your First Analysis
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAnalyses.map((analysis) => (
                <Link 
                  key={analysis.analysis_id} 
                  to={`/analysis/${analysis.analysis_id}`}
                  className="block"
                  data-testid={`analysis-card-${analysis.analysis_id}`}
                >
                  <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.08)] transition-all group">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#DCEBFF] rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-[#0B3D91]" />
                        </div>
                        <div>
                          <h3 className="font-outfit font-semibold text-[#0B3D91] group-hover:text-[#1E5EFF] transition-colors">
                            {analysis.name || 'Single Product Analysis'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(analysis.created_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Package className="w-4 h-4" />
                              {analysis.total_skus} SKUs
                            </span>
                            {analysis.optimized_skus > 0 && (
                              <span className="flex items-center gap-1 text-[#10B981]">
                                <TrendingUp className="w-4 h-4" />
                                {analysis.optimized_skus} optimized
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {analysis.total_estimated_savings > 0 && (
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Est. Savings</p>
                            <p className="font-outfit font-bold text-[#10B981]">
                              ${analysis.total_estimated_savings.toLocaleString()}
                            </p>
                          </div>
                        )}
                        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#1E5EFF] group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CatalogHistory;
